import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Calendar, Heart, Home, LogOut, Wallet, Plug, Key, Building2, Ticket,
  Users, ChevronRight, ShoppingBag, Nfc, DoorOpen,
} from 'lucide-react';

/**
 * AnimatedIcon is lazy-loaded so lottie-react ships in its own
 * chunk and stays out of the main PWA bundle until a Profile
 * sheet opens. Per-row Suspense fallbacks render the static
 * Lucide icon during the chunk's first-open fetch — same UX as
 * a row whose JSON file isn't authored yet, so the loading
 * state is invisible.
 */
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { usePinnedShortcuts } from '@/hooks/usePinnedShortcuts';

const AnimatedIcon = lazy(() => import('@/components/ui/AnimatedIcon'));

/**
 * Per-row icon helper. Renders <AnimatedIcon> behind a Suspense
 * boundary whose fallback IS the Lucide icon — three benefits:
 *   1. Lottie chunk loads only when a sheet opens.
 *   2. While that chunk loads, every row shows its Lucide icon
 *      (no spinner, no layout shift).
 *   3. Once the chunk lands, AnimatedIcon mounts and either
 *      animates (JSON present at /icons/{name}.json) or shows
 *      the same Lucide as the suspense fallback (JSON missing).
 *
 * Sheet rows use 30px icons (bumped from 16 — at 16 the icons
 * read as small twitchy details; at 30 they're clearly visible
 * while staying subordinate to the 80px parent CategoryRow
 * icons in the visual hierarchy). The size is hard-coded here
 * rather than passed per call site to keep the wiring concise.
 */
const SHEET_ICON_SIZE_PX = 30;
/** Stagger between adjacent rows in a sheet. Matches the parent-row
 *  CATEGORY_STAGGER_MS below. */
const SHEET_STAGGER_MS = 150;
function SheetIcon({
  name, fallback, size = SHEET_ICON_SIZE_PX, staggerDelayMs = 0,
}: { name: string; fallback: ReactNode; size?: number; staggerDelayMs?: number }) {
  return (
    <Suspense fallback={fallback}>
      <AnimatedIcon
        src={`/icons/${name}.json`}
        fallback={fallback}
        size={size}
        // Phase 2 policy: every slot loops continuously. Sheet rows
        // previously played once on open + tap per the old README;
        // founder ratified the loop default this round. Tap still
        // replays from frame zero via AnimatedIcon's tap handler.
        playOnMount
        playOnTap
        loop
        playDelay={staggerDelayMs}
      />
    </Suspense>
  );
}

/* The four Profile PARENT chips (EVI Rewards / My Charging / Money / Account)
 * moved off the Lottie CategoryIcon helper onto the MenuIcon animated-WebP
 * primitive (icon-family conversion): the img fills the .categoryIcon chip and
 * inherits its rounded masking; posters swap in under prefers-reduced-motion.
 * SheetIcon below still serves the sheet rows that remain on Lottie. */
import {
  fetchProfile, fetchCashWallet, buildAvatarUrl, logout,
  fetchAccount, fetchPaymentMethods, leaveFleet, leaveBuilding,
} from '@/lib/api';
import { cashWalletQueryKey } from '@/lib/cashWalletKey';
import { APP_VERSION_LABEL } from '@evi/shared';
import { SwitchBuildingModal } from '@/components/ui/SwitchBuildingModal';
import { BottomSheet } from '@evi/ui';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { handleRequiresRelogin } from '@/lib/relogin';
import { useCurrentMode } from '@/hooks/useCurrentMode';
import { AvatarPicker } from '@/components/AvatarPicker';
import { NonDriverBanner } from '@/components/NonDriverBanner';
import { HeroCard, ListRow, MenuIcon, StatusPill, TokenSpin } from '@/components/primitives';
import styles from './ProfilePage.module.css';

/**
 * Reliable tap-to-navigate for the glass menu cards. A `<button onClick>`
 * relies on the browser-synthesized click, which iOS SUPPRESSES on a
 * touch-and-hold (the long-press is treated as a callout / selection gesture),
 * so a held press shows :active but never fires onClick. This drives the nav
 * from pointerup instead: a touch of ANY duration that did not move into a
 * scroll navigates on release, and the following synthetic click is de-duped
 * so mouse / keyboard still fire exactly once. 12px = the move-into-scroll
 * cancel threshold. No long-press special behaviour — held taps just work.
 */
function usePressNav(onNav: () => void, onLongPress?: () => void) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const moved = useRef(false);
  const viaPointer = useRef(false);
  const longFired = useRef(false);
  const timer = useRef<number | null>(null);
  const clearTimer = (): void => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      start.current = { x: e.clientX, y: e.clientY };
      moved.current = false;
      longFired.current = false;
      clearTimer();
      if (onLongPress) {
        // ~500ms hold (touch OR mouse click-and-hold) opens the sheet PINNED.
        // Cancelled on release under 500ms or on a scroll move past 12px, so a
        // normal tap is unaffected. The any-duration tap fix below still holds:
        // a held tap that stays under the move threshold pins on fire, a shorter
        // one navigates on release, movement into a scroll cancels both.
        timer.current = window.setTimeout(() => {
          if (start.current && !moved.current) {
            longFired.current = true;
            onLongPress();
          }
        }, 500);
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!start.current) return;
      if (Math.abs(e.clientX - start.current.x) > 12
        || Math.abs(e.clientY - start.current.y) > 12) {
        moved.current = true;
        clearTimer();
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      clearTimer();
      const tapped = start.current !== null && !moved.current;
      start.current = null;
      if (longFired.current) {
        // Long-press already opened the sheet pinned — suppress the tap-nav and
        // the trailing synthetic click.
        viaPointer.current = true;
        return;
      }
      if (tapped && e.pointerType === 'touch') {
        viaPointer.current = true;
        onNav();
      }
    },
    onPointerCancel: () => { start.current = null; moved.current = false; clearTimer(); },
    onClick: () => {
      if (viaPointer.current) { viaPointer.current = false; return; }
      if (longFired.current) { longFired.current = false; return; }
      onNav();
    },
  };
}

/** Read the pinned-sheet key from the URL (?pinned=<key>) — the carrier for
 *  pinned-ness, so the history stack restores the sheet on back. */
function readPinnedSheet(): OpenSheet {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search).get('pinned');
  return p === 'charging' || p === 'money' || p === 'account' ? p : null;
}

interface Props { onNavigate: (path: string) => void }

/** Pull the structured `error.code` out of a ky HTTPError body (null for
 *  network errors or unparseable bodies). Same shape JoinFleetPage and
 *  SwitchBuildingModal read; kept local so the two leave handlers can
 *  branch on a code without another api.ts export. */
async function readApiErrorCode(err: unknown): Promise<string | null> {
  const response = (err as { response?: Response }).response;
  if (!response) return null;
  try {
    const body = await response.clone().json() as { error?: { code?: string } };
    return body.error?.code ?? null;
  } catch {
    return null;
  }
}

/**
 * Which category bottom-sheet (if any) is currently open. `null`
 * means no sheet is up — the three category rows + the direct
 * EVI Rewards link are the page-level entry points.
 *
 * "Rewards & Activity" was retired as a category — it only ever
 * fronted the EVI Rewards destination in practice (Favorites was
 * moved into My Charging where it sits naturally alongside
 * "My Chargers" / "Chargers I Can Use").
 */
type OpenSheet = null | 'charging' | 'account' | 'money';

export function ProfilePage({ onNavigate }: Props) {
  const { t } = useTranslation('menu');
  const { t: tShortcuts } = useTranslation('shortcuts');
  // The driver's three home-screen pins — the same three the map FAB
  // opens (frictionless flow, piece 1). First row of the Menu.
  const { pins } = usePinnedShortcuts();
  const user = useAuthStore((s) => s.user);
  const refreshUser = useAuthStore((s) => s.refreshUser);
  // Phase 3 — mode toggle. canSwitch is true iff the user has a
  // fleet attribution; the Account-sheet Fleet Mode row + top-nav
  // pill both gate on this and read from the same hook so their
  // state never drifts.
  const { mode: currentMode, canSwitch, toggle: toggleMode, isSwitching: modeSwitching } = useCurrentMode();
  // Phase 6 — personal-category visibility (EVI Rewards, Money).
  // Pre-Phase-6 the gate was `!inFleetShell`, which conflated "is
  // this driver fleet-attributed" with "is this driver currently
  // billing the fleet." Phase 3 introduced the mode mechanism that
  // splits the two: a fleet driver in 'public' mode IS accruing
  // personal points / wallet balance and SHOULD see those rows. The
  // new gate keys off currentMode === 'public' (which is also true
  // for non-fleet drivers, who default to 'public' forever). (The
  // Account sheet no longer reads the fleet-shell flag at all: one
  // "Join a building or fleet" row serves every driver, and the
  // "My Fleet" row keys off user.fleet_id directly.)
  const showPersonalCategories = currentMode === 'public';
  const profileQ = useQuery({ queryKey: ['profile'], queryFn: fetchProfile });
  const profile = profileQ.data;
  // Live wallet balance for the Loyalty Wallet row — 30s staleTime keeps
  // it feeling fresh without spamming /wallet on every profile re-enter.
  // Keyed per signed-in user (cashWalletQueryKey), as the wallet page is.
  const walletQ = useQuery({
    queryKey: cashWalletQueryKey(user?._id),
    queryFn: fetchCashWallet,
    staleTime: 30_000,
  });
  // Payment methods drive the Money submenu's Payments-row trailing
  // pill — render the user's actual default card brand + last4 instead
  // of the previous hardcoded "VISA •••• 4821" deception. Shared cache
  // key with PaymentMethodsPage + PreSessionPage so the data is hot
  // whenever the user has visited either; 60s staleTime keeps Profile
  // mounts cheap. When no default card is on file (or query is still
  // loading / errored), the pill simply omits — vague-but-honest beats
  // a placeholder that lies.
  const paymentMethodsQ = useQuery({
    queryKey: ['payment-methods'],
    queryFn: fetchPaymentMethods,
    staleTime: 60_000,
  });
  const defaultCard = paymentMethodsQ.data?.payment_methods.find(
    (pm) => pm.id === paymentMethodsQ.data?.default_payment_method,
  );
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [switchBuildingOpen, setSwitchBuildingOpen] = useState(false);
  // Single-bottom-sheet-at-a-time state. The four category rows
  // each set this to their own key on tap; the sheet's onClose
  // resets it to null. The nested SwitchBuildingModal opens ON TOP
  // of the Account sheet, but its close handler also dismisses the
  // parent sheet to avoid a stacked-sheet UX. (The phone editor
  // moved to Account Settings with its row.)
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);

  // Guided-tour bridge: the My Charging step opens this page's charging sheet
  // so EVII can point at what's inside (Friends, sessions, chargers). A window
  // CustomEvent rather than a store hookup keeps the tour decoupled from this
  // page's local sheet state.
  useEffect(() => {
    const open = (): void => setOpenSheet('charging');
    window.addEventListener('evi:tour:open-charging-sheet', open);
    return () => window.removeEventListener('evi:tour:open-charging-sheet', open);
  }, []);

  // Pinned (sticky) submenu — carried in the URL (?pinned=<key>). The router is
  // history-based (pushState/popstate), so a query param rides the history
  // stack: BACK from a destination lands on /profile?pinned=<key> and this page
  // remounts with the sheet restored; a local flag couldn't (the page unmounts
  // on navigate away). A second BACK / the X / the scrim unwind the pushed pin
  // entry to the bare /profile. Long-press pins (see usePressNav).
  const [pinnedSheet, setPinnedSheet] = useState<OpenSheet>(() => readPinnedSheet());
  useEffect(() => {
    // A same-path back (…?pinned=X -> bare /profile) doesn't remount this page,
    // so sync the pin from the URL on every popstate.
    const onPop = (): void => setPinnedSheet(readPinnedSheet());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Account state — ack flags and friends. 60s staleTime is long
  // enough that re-entering Profile doesn't re-fetch on every
  // render. (The phone row moved to Account Settings.)
  const accountQ = useQuery({
    queryKey: ['account'],
    queryFn: fetchAccount,
    staleTime: 60_000,
  });

  // (DriverThemePicker has been lifted to the AppShell global top
  // bar via DriverThemePickerContainer — Profile no longer owns
  // its own picker mount or the related entry-building / handler
  // scaffold. The container reads the same shared `profile` +
  // `unlocks` query keys, so themes applied from the global picker
  // reflect here on the next render without extra wiring.)

  const points = profile?.points ?? 0;
  const rankName = (profile?.current_rank ?? 'Silver').toUpperCase();
  const nextRank = (profile?.next_rank ?? 'Gold').toUpperCase();
  const pointsToNext = profile?.points_to_next_rank ?? 0;
  const displayName = (profile?.username ?? user?.name ?? 'Driver').toUpperCase();

  // In-app logout confirm — replaces the browser-native confirm()
  // (the "localhost says" dialog) with the app's own ConfirmDialog.
  // The Account sheet's Log out row opens it; confirm runs the real
  // logout, cancel just closes.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const handleLogout = async (): Promise<void> => {
    await logout();
    onNavigate('/login');
  };

  // Leave fleet / leave building confirms. Both throw a translated
  // Error on failure so ConfirmDialog renders it inline and stays open
  // for a retry; success closes the dialog from here.
  const [confirmLeaveFleet, setConfirmLeaveFleet] = useState(false);
  const [confirmLeaveBuilding, setConfirmLeaveBuilding] = useState(false);
  // The current operator-managed building, or null when the driver is in
  // the default placeholder tenant (nothing to leave) or /me/account is
  // still loading. Drives the "Leave building" row + its confirm copy.
  const currentBuilding = accountQ.data?.current_tenant && !accountQ.data.current_tenant.is_default
    ? accountQ.data.current_tenant
    : null;

  const handleLeaveFleet = async (): Promise<void> => {
    const fleetName = user?.fleet_name ?? null;
    try {
      const res = await leaveFleet();
      // refreshUser drops fleet_id / current_mode so the My Fleet row,
      // Fleet Mode toggle and top-nav pill all clear on the same render.
      await refreshUser();
      setConfirmLeaveFleet(false);
      const name = res.fleet_name ?? fleetName;
      toast(
        name ? t('leaveFleet.success', { fleetName: name }) : t('leaveFleet.successNoName'),
        { tone: 'accent' },
      );
    } catch (err) {
      if (await readApiErrorCode(err) === 'NOT_IN_FLEET') {
        // Already out (admin removal or another device) — the store is
        // stale, not the request. Refresh and close quietly.
        await refreshUser();
        setConfirmLeaveFleet(false);
        toast(t('leaveFleet.errors.notInFleet'), { tone: 'warn' });
        return;
      }
      throw new Error(t('leaveFleet.errors.generic'));
    }
  };

  const handleLeaveBuilding = async (): Promise<void> => {
    try {
      const res = await leaveBuilding();
      setConfirmLeaveBuilding(false);
      if (res.requires_relogin) {
        // Tenant moved → the access token's tenant_id claim is stale and
        // the server already cleared the refresh cookie. Same plumbing
        // the join-building modal uses, with our own translated line so
        // the bounce says "left", not "changed".
        handleRequiresRelogin(onNavigate, {
          message: t('leaveBuilding.relogin', { name: currentBuilding?.name ?? '' }),
        });
        return;
      }
      // Defensive — the server always sets requires_relogin today.
      window.location.reload();
    } catch (err) {
      const code = await readApiErrorCode(err);
      if (code === 'LEAVE_FLEET_FIRST') throw new Error(t('leaveBuilding.errors.leaveFleetFirst'));
      if (code === 'ADMIN_GRANT_PRESENT') {
        throw new Error(t('leaveBuilding.errors.adminGrant', { name: currentBuilding?.name ?? '' }));
      }
      if (code === 'NOT_IN_BUILDING') throw new Error(t('leaveBuilding.errors.notInBuilding'));
      throw new Error(t('leaveBuilding.errors.generic'));
    }
  };

  const avatarSrc = profile?.avatar_url
    ?? buildAvatarUrl(profile?.avatar_style ?? 'avataaars-neutral', profile?.avatar_seed ?? user?.email ?? 'default');

  /**
   * Helper: navigate AND dismiss the open bottom sheet on the same
   * tap. Tapping a sub-item in the sheet should both route to the
   * destination and clean up the overlay state — without this,
   * back-navigation would land back on Profile with the sheet
   * still open.
   */
  const navAndClose = (path: string): void => {
    // A pinned sheet keeps its /profile?pinned entry in history (navigate just
    // pushes the destination on top), so BACK restores it. A transient sheet
    // clears here so BACK lands on the bare Profile — unchanged behaviour.
    setOpenSheet(null);
    onNavigate(path);
  };

  // Long-press a category card opens its sheet PINNED: push (or replace, when
  // already pinned) a /profile?pinned=<key> entry so a later BACK restores it,
  // plus a brief haptic (silently skipped where navigator.vibrate is absent).
  const pinSheet = (key: Exclude<OpenSheet, null>): void => {
    const url = `/profile?pinned=${key}`;
    if (readPinnedSheet()) window.history.replaceState({}, '', url);
    else window.history.pushState({}, '', url);
    setPinnedSheet(key);
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  };
  // Close from the X / scrim / swipe. A pinned sheet unwinds its pushed history
  // entry (back to the bare /profile, which the popstate sync reflects); a
  // transient tap-opened sheet just clears the local open state.
  const closeSheet = (key: Exclude<OpenSheet, null>): void => {
    if (readPinnedSheet() === key) {
      // Unpin: strip ?pinned from the CURRENT history entry and close.
      // replaceState (not history.back) so it also works when the pinned entry
      // is first in history — a refresh or deep-link of /profile?pinned=X,
      // where back() would leave the app / have nowhere to go and the sheet
      // could never be closed. Browser BACK still unpins on its own: it pops to
      // the bare /profile the pin pushed, and the popstate sync clears the pin.
      window.history.replaceState({}, '', '/profile');
      setPinnedSheet(null);
    } else {
      setOpenSheet(null);
    }
  };

  // Reliable tap-to-navigate for the points card — same held-tap fix as the
  // menu cards (see usePressNav).
  const rewardsPress = usePressNav(() => onNavigate('/rewards'));

  return (
    <div className={styles.page}>
      {/* Non-driver hint banner — same component as History.
          Invisible for production drivers; visible for ops /
          dev sessions where the operator is signed in as a
          non-driver role testing the PWA. */}
      <NonDriverBanner />
      {/* Brand chrome (logo + wordmark + theme picker) lifted to
          the AppShell global top bar — Profile starts straight
          into the HeroCard now. The .brandHeader CSS rule is
          retired alongside this element.

          Wrapped in .heroEntry so the Disney-12 #2 Anticipation
          spring runs on mount. HeroCard itself doesn't take a
          className prop; the wrapper carries the animation. */}
      <div className={styles.heroEntry}>
        <HeroCard
          avatar={
            // No edit-pencil badge: it covered part of the avatar art. The
            // avatar stays tappable (onAvatarClick -> picker); discoverable
            // without the overlay.
            <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          }
          onAvatarClick={() => setAvatarPickerOpen(true)}
          title={displayName}
          subtitle={user?.email ?? ''}
        />
      </div>

      {/* Points card — 3 cols: icon | info | actions. The actions
          column stacks the rank pill above the Redeem button so the
          card reads cleanly even with the larger 68px TokenSpin.
          Phase 6 — gated on showPersonalCategories (currentMode ===
          'public'). Fleet drivers in fleet mode bill the fleet and
          don't accrue personal points on those sessions, so the
          card would read as zero-progress / dead-end UX. The same
          predicate gates the EVI Rewards + Money CategoryRows
          below, so the personal-rewards surface appears or
          disappears as a coherent unit on mode flip. Non-fleet
          drivers default to 'public' forever and always see the
          card. The card is a sibling of .categoryList — its
          visibility doesn't affect the category-row stagger calc
          (which is internal to that list). */}
      {showPersonalCategories && (
      <button
        type="button"
        className={styles.pointsCard}
        {...rewardsPress}
      >
        <span className={styles.pointsIcon}>
          {/* 68px — coin reads as the row's visual anchor. The
              container width/height match so the grid track sizes
              correctly and the translateY nudge stays meaningful. */}
          <TokenSpin size={68} />
        </span>
        <span className={styles.pointsInfo}>
          <span className={styles.pointsValue}>{points.toFixed(2)}</span>
          <span className={styles.pointsLabel}>{t('points.unit')}</span>
        </span>
        <span className={styles.pointsActions}>
          <span className={styles.pointsRankRow}>
            <StatusPill tone="mute" variant="soft" size="sm">{rankName}</StatusPill>
            {pointsToNext > 0 && (
              <span className={styles.pointsToNext}>
                {t('points.toNext', { points: pointsToNext.toFixed(2), rank: nextRank })}
              </span>
            )}
          </span>
          <StatusPill tone="accent" variant="outline" size="md">{t('points.redeem')}</StatusPill>
        </span>
      </button>
      )}

      {/* ── Category rows ──────────────────────────────────────────
          Direct EVI Rewards link sits where the "Rewards &
          Activity" category used to — the sub-categorization
          was over-organization for a single real destination.
          Favorites moved into My Charging (it sits naturally
          alongside "My Chargers" / "Chargers I Can Use"). */}
      {/* Each parent row's icon is an 80px AnimatedIcon that loops
          continuously. The four cards stagger their initial play
          (0/150/300/450ms) so they don't start in lockstep on
          Profile mount; with native loop on, that offset persists
          and the four animations run independently from there.
          With no JSON files at /icons/{rewards,charging,account,
          money}.json the row falls back to the Lucide icon shown
          below — same visual size, just static. */}
      {/* ── Your shortcuts ──────────────────────────────────────────
          The three pins, then the category rows. Edit → /shortcuts. */}
      <div className={styles.pinsBlock}>
        <div className={styles.pinsHead}>
          <span className={styles.pinsTitle}>{tShortcuts('menuTitle')}</span>
          <button type="button" className={styles.pinsEdit} onClick={() => onNavigate('/shortcuts')}>
            {tShortcuts('edit')}
          </button>
        </div>
        <div className={styles.pinsRow} data-tour="shortcuts-row">
          {pins.map((p) => (
            <button
              key={p.id}
              type="button"
              className={styles.pinChip}
              onClick={() => onNavigate(p.href)}
            >
              <span className={styles.pinChipIcon} aria-hidden>{p.icon}</span>
              <span className={styles.pinChipLabel}>{tShortcuts(p.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.categoryList}>
        {/* Notifications row removed — the global top bar's
            NotificationBell (commit 2257533) is the single
            entry point now, with "View all" routing into
            /notifications when the driver wants the full list.
            Promoting the bell to chrome makes the menu row
            redundant; keeping both would split the awareness
            signal across two surfaces. EVI Rewards is the new
            top-of-menu by default. */}
        {/* Phase 6 — gate Rewards + Money on the per-session billing
            mode rather than fleet membership. A fleet driver in
            'public' mode accrues personal points + wallet balance
            on those sessions, so hiding the categories would
            dead-end them. Non-fleet drivers default to 'public'
            forever and always see both. Stagger delays collapse
            onto the visible subset so the entry animation doesn't
            have phantom gaps. */}
        {showPersonalCategories && (
          <CategoryRow
            icon={<MenuIcon name="rewards" fill alt={t('category.rewards.label')} />}
            label={t('category.rewards.label')}
            subtitle={t('category.rewards.subtitle')}
            onClick={() => onNavigate('/rewards')}
            dataTour="rewards-row"
          />
        )}
        <CategoryRow
          icon={<MenuIcon name="charging" fill alt={t('category.charging.label')} />}
          label={t('category.charging.label')}
          subtitle={t('category.charging.subtitle')}
          onClick={() => setOpenSheet('charging')}
          onLongPress={() => pinSheet('charging')}
        />
        {showPersonalCategories && (
          <CategoryRow
            icon={<MenuIcon name="wallet" fill alt={t('category.money.label')} />}
            label={t('category.money.label')}
            subtitle={t('category.money.subtitle')}
            onClick={() => setOpenSheet('money')}
            onLongPress={() => pinSheet('money')}
          />
        )}
        <CategoryRow
          icon={<MenuIcon name="account" fill alt={t('category.account.label')} />}
          label={t('category.account.label')}
          subtitle={t('category.account.subtitle')}
          onClick={() => setOpenSheet('account')}
          onLongPress={() => pinSheet('account')}
        />
      </div>

      <p className={styles.version}>{APP_VERSION_LABEL}</p>

      {/* ── Bottom sheets ──────────────────────────────────────────
          One sheet per category. Each holds the same sub-items
          that used to render inline; tapping a sub-item navigates
          AND dismisses the sheet via navAndClose(). */}

      <BottomSheet
        isOpen={openSheet === 'charging' || pinnedSheet === 'charging'}
        onClose={() => closeSheet('charging')}
        title={t('category.charging.label')}
        size="wide"
        glass
        pinned={pinnedSheet === 'charging'}
      >
        {/* Friends leads the submenu. The previous adjacent-to-
            "Chargers I Can Use" placement framed it as the
            symmetric counterpart to received access; that's a
            valid lens but it puts the social entry point fifth,
            buried under the records-and-management cluster.
            Promoting it to the top reflects what Friends actually
            is in the product: the social graph that everything
            else in this submenu downstream connects through —
            Favorites and Charging History are individual records,
            My Chargers / Chargers I Can Use are sharing surfaces
            on top of the social graph, My EV is personal identity.
            Friends is the seam they all touch, so it leads.
            Trailing unread-count badge still omitted for v1;
            ambient awareness of pending friend requests lives on
            the Profile tab dot via useNotificationsUnreadCount. */}
        <ListRow
          icon={<SheetIcon name="users" fallback={<Users size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={0} />}
          label={t('charging.friends')}
          onClick={() => navAndClose('/friends')}
        />
        {/* Favorites — second slot. Was the lead before Friends
            was promoted; still the highest-frequency individual-
            record destination ("places I keep coming back to")
            so it sits right under the social entry point. */}
        <ListRow
          icon={<SheetIcon name="heart" fallback={<Heart size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS} />}
          label={t('charging.favorites')}
          onClick={() => navAndClose('/favorites')}
        />
        {/* Charging History — moved directly below Favorites (was
            between My Chargers and 3rd-Party): the session record is
            the higher-frequency destination, so it rides right under
            the places the driver keeps returning to. Renamed from
            "My Bookings" earlier; route (/history) unchanged. Icon
            sized 40 vs the sheet's default 30 because the current
            history.json (calendar1.json clone) has heavy internal
            padding that reads tiny at the default scale. */}
        <ListRow
          icon={<SheetIcon name="history" fallback={<Calendar size={40} />} size={40} staggerDelayMs={SHEET_STAGGER_MS * 2} />}
          label={t('charging.history')}
          onClick={() => navAndClose('/history')}
        />
        <ListRow
          icon={<SheetIcon name="plug" fallback={<Plug size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS * 3} />}
          label={t('charging.myChargers')}
          onClick={() => navAndClose('/my-chargers')}
          // Tour anchor: the core-mycharging step flies EVII here — the middle
          // of the sheet's rows — so she touches what she's describing instead
          // of parking on the top-pinned dialog card.
          dataTour="mycharging-rows"
        />
        {/* Renamed from "3rd-Party Chargers I Can Use" — shorter label,
            same /my-access route and page. */}
        <ListRow
          icon={<SheetIcon name="key" fallback={<Key size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS * 4} />}
          label={t('charging.thirdParty')}
          onClick={() => navAndClose('/my-access')}
        />
        {/* The hidden arcade deliberately has NO menu row, even once
            unlocked — entry is EVII's redeem_code button or a direct
            /arcade URL (bookmark / home-screen). The route itself
            stays flag-gated and reachable. */}
        {/* My EV moved to the Settings page (Residential UX slice A):
            vehicle identity sits with profile preferences, not the
            charging workflow rows. Route /vehicle unchanged. */}
        {/* My Home -- the driver's residential home setup (My Home slice A).
            Personal-place row. Routes to /my-home; /home is the dashboard
            tab, so the page lives at /my-home. */}
        {/* My Home's Lottie has a 1920x1080 (16:9) canvas, so lottie-web
            letterboxes it into the square icon box and the house reads tiny.
            Fill the fixed 40px ListRow icon box: raise the size prop and zoom
            the letterboxed art with a clipped scale scoped to THIS row only
            (.homeIconFill clips the overflow). Box, row height, and every
            other row are unchanged. */}
        <ListRow
          icon={
            <span className={styles.homeIconFill}>
              <SheetIcon
                name="house home building maison 001 mocca animation"
                fallback={<Home size={22} />}
                size={40}
                staggerDelayMs={SHEET_STAGGER_MS * 5}
              />
            </span>
          }
          label={t('charging.myHome')}
          onClick={() => navAndClose('/my-home')}
        />
        {/* Store -- buy a home charger. Sits after My Home in the My Charging
            sheet, framed as part of home setup. Routes to the in-app catalog. */}
        <ListRow
          icon={<SheetIcon name="shop" fallback={<ShoppingBag size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS * 6} />}
          label={t('charging.store')}
          onClick={() => navAndClose('/store')}
        />
        {/* My Orders moved to the store header (its package icon on the
            catalog, detail, ICP, cart, and success pages) — the store is
            the natural home for the commerce chrome, so the menu row was
            removed to avoid a second entry point. The /store/orders page
            and route are unchanged; EVII can still deep-link there. */}
      </BottomSheet>

      <BottomSheet
        isOpen={openSheet === 'account' || pinnedSheet === 'account'}
        onClose={() => closeSheet('account')}
        title={t('category.account.label')}
        size="wide"
        glass
        pinned={pinnedSheet === 'account'}
      >
        {/* Fleet member badge (Phase 1 fleet platform). Renders at
            the top of the Account sheet when the driver is fleet-
            attributed so they can see their membership at a glance.
            Read-only — fleet assignment is operator-driven in v1. */}
        {user?.fleet_id && user.fleet_name && (
          <div
            style={{
              margin: '0 0 12px',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 30%, transparent)',
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            <span style={{ color: 'var(--color-tx-m, rgba(255,255,255,0.7))' }}>
              {t('account.fleetMember')}
            </span>
            <strong style={{ color: 'var(--color-tx)' }}>{user.fleet_name}</strong>
          </div>
        )}
        <ListRow
          icon={<MenuIcon name="settings" fill alt={t('account.settings')} />}
          label={t('account.settings')}
          onClick={() => navAndClose('/settings')}
        />
        <ListRow
          icon={<MenuIcon name="help" fill alt={t('account.help')} />}
          label={t('account.help')}
          onClick={() => navAndClose('/help')}
        />
        {/* "Switch building" → "Register building" → "Join a Building" →
            "Join a building or fleet". One entry point for every driver:
            the SwitchBuildingModal now takes a 6-char building code OR an
            8-char fleet invite code and routes on length. Previously the
            fleet row only existed inside the fleet shell, so a normal
            driver handed a fleet code had nowhere to type it — the
            building modal hard-capped input at 6 and truncated it. The
            wallet-pinned warning copy still fires for users actively
            switching tenants. */}
        {/* Plain Lucide-style MenuIcon rather than AnimatedIcon — the
            cityscape /icons/building.json drives the modal's 140px hero,
            so animating this small sheet entry too was redundant. */}
        <ListRow
          icon={<MenuIcon name="building" fill alt={t('account.joinBuildingOrFleet')} />}
          label={t('account.joinBuildingOrFleet')}
          onClick={() => {
            // closeSheet (not setOpenSheet) so a PINNED Account sheet unpins
            // before the modal opens — matches the transient behaviour and
            // avoids leaving the sheet stacked behind the modal.
            closeSheet('account');
            setSwitchBuildingOpen(true);
          }}
        />
        {/* Phase 3b Item 1 / Item 3 — "My Fleet" link → /me/fleet-stats
            (driver mini-dashboard). Keyed off user.fleet_id directly, no
            longer the fleet-shell flag: the shell can be forced by
            hostname / cookie with no attribution, and this row is about
            membership. */}
        {user?.fleet_id && user.fleet_name && (
          <ListRow
            icon={<Building2 size={SHEET_ICON_SIZE_PX} />}
            label={t('account.myFleet')}
            meta={
              <span style={{ fontSize: 13, color: 'var(--evi-text-mute)' }}>
                {user.fleet_name}
              </span>
            }
            onClick={() => {
              setOpenSheet(null);
              onNavigate('/me/fleet-stats');
            }}
          />
        )}
        {/* Fleet Mode toggle (Phase 3.5 consolidation). Sits
            directly below "My Fleet" so the membership row + the
            mode-flip control read as a single unit — the user sees
            "I belong to Test Fleet Co." → "right now I'm billing
            it / not billing it." Only fleet-attributed drivers see
            this row (gates on canSwitch); non-fleet drivers don't
            see "My Fleet" either, so the pair stays co-located.
            Tap → useCurrentMode().toggle (same hook the top-nav
            pill reads), so flipping here updates the pill on the
            same render via the shared zustand auth store. */}
        {canSwitch && (
          <ListRow
            icon={<SheetIcon name="fleet-mode" fallback={<Plug size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS} />}
            label={t('account.fleetMode')}
            meta={
              <ModeStatusPill
                active={currentMode === 'fleet'}
                activeLabel={t('account.fleetModeActive')}
                inactiveLabel={t('account.fleetModeInactive')}
                disabled={modeSwitching}
              />
            }
            onClick={() => { void toggleMode(); }}
          />
        )}
        {/* Self-serve exits (fleet joining without friction). Both are
            in-flow rows in the same danger register as Log out, each
            behind a ConfirmDialog. ListRow is itself a <button>, so the
            action can't nest inside the "My Fleet" row; it gets its own
            row with the fleet / building name as the subtitle instead.
              - Leave fleet    → POST /fleets/leave, refreshUser, toast.
                                 Only while fleet-attributed.
              - Leave building → POST /auth/leave-building; the tenant
                                 moves back to the default one, so it
                                 always ends in a re-login. Only while
                                 in an operator-managed (non-default)
                                 tenant — the placeholder tenant has
                                 nothing to leave to. */}
        {user?.fleet_id && user.fleet_name && (
          <ListRow
            icon={<DoorOpen size={SHEET_ICON_SIZE_PX} />}
            label={t('account.leaveFleet')}
            subtitle={user.fleet_name}
            tone="danger"
            hideChevron
            onClick={() => {
              closeSheet('account');
              setConfirmLeaveFleet(true);
            }}
          />
        )}
        {currentBuilding && (
          // The server refuses LEAVE_FLEET_FIRST while fleet_id is set,
          // so a fleet driver gets the row disabled with the reason as
          // its subtitle instead of a confirm that can only fail.
          // ListRow renders disabled when it has no onClick.
          <ListRow
            icon={<Building2 size={SHEET_ICON_SIZE_PX} />}
            label={t('account.leaveBuilding')}
            subtitle={user?.fleet_id ? t('leaveBuilding.leaveFleetFirstHint') : currentBuilding.name}
            tone="danger"
            hideChevron
            onClick={user?.fleet_id ? undefined : () => {
              closeSheet('account');
              setConfirmLeaveBuilding(true);
            }}
          />
        )}
        {/* LOG OUT moved here from the main Profile page — fits
            naturally alongside Settings + the rest of the account-
            shaped surface, and removes one item from the page-
            level scroll. The divider above visually separates the
            destructive action from the navigational rows. */}
        <div className={styles.sheetDivider} aria-hidden />
        <ListRow
          icon={<SheetIcon name="logout" fallback={<LogOut size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS * 2} />}
          label={t('account.logout')}
          tone="danger"
          onClick={() => {
            // closeSheet so a PINNED Account sheet unpins before the confirm
            // dialog (consistent with the transient close).
            closeSheet('account');
            setConfirmLogout(true);
          }}
        />
      </BottomSheet>

      {/* Styled logout confirm — ConfirmDialog handles the async
          confirm (spinner while logout resolves) and blocks implicit
          dismissal, matching the app's destructive-decision pattern. */}
      {confirmLogout && (
        <ConfirmDialog
          title={t('logout.title')}
          body={t('logout.body')}
          confirmLabel={t('logout.confirm')}
          confirmTone="danger"
          onConfirm={handleLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}

      {confirmLeaveFleet && user?.fleet_name && (
        <ConfirmDialog
          title={t('leaveFleet.title', { fleetName: user.fleet_name })}
          body={t('leaveFleet.body')}
          confirmLabel={t('leaveFleet.confirm')}
          confirmTone="danger"
          onConfirm={handleLeaveFleet}
          onCancel={() => setConfirmLeaveFleet(false)}
        />
      )}

      {confirmLeaveBuilding && currentBuilding && (
        <ConfirmDialog
          title={t('leaveBuilding.title', { name: currentBuilding.name })}
          body={t('leaveBuilding.body', { name: currentBuilding.name })}
          confirmLabel={t('leaveBuilding.confirm')}
          confirmTone="danger"
          onConfirm={handleLeaveBuilding}
          onCancel={() => setConfirmLeaveBuilding(false)}
        />
      )}

      <BottomSheet
        isOpen={openSheet === 'money' || pinnedSheet === 'money'}
        onClose={() => closeSheet('money')}
        title={t('category.money.label')}
        size="wide"
        glass
        pinned={pinnedSheet === 'money'}
      >
        {/* Payments row trailing pill reads the user's REAL default
            card from /billing/payment-methods (paymentMethodsQ above).
            Previous hardcoded "VISA •••• 4821" pill was visible
            deception (mock-data hunt adjacent finding to #5/#7).
            Pill omits entirely when:
              - No default flagged on the response
              - No payment methods on file at all
              - Query still loading / errored
            All four cases collapse to the same UX: row reads
            "Payments →" with no trailing meta, which is honest
            (we don't know / they don't have one) and routes to
            the full /billing/payment-methods page where they can
            add or change cards. */}
        <ListRow
          icon={<MenuIcon name="payment" fill alt={t('money.payments')} />}
          label={t('money.payments')}
          meta={defaultCard?.brand && defaultCard.last4 ? (
            <StatusPill tone="mute" variant="soft">
              {defaultCard.brand.toUpperCase()} •••• {defaultCard.last4}
            </StatusPill>
          ) : null}
          onClick={() => navAndClose('/billing/payment-methods')}
        />
        <ListRow
          icon={<SheetIcon name="wallet" fallback={<Wallet size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS} />}
          label={t('money.wallet')}
          meta={
            <span
              className={styles.walletBalance}
              aria-label={`Wallet balance: $${((walletQ.data?.balanceCents ?? 0) / 100).toFixed(2)}`}
            >
              {walletQ.isLoading
                ? '—'
                : `$${((walletQ.data?.balanceCents ?? 0) / 100).toFixed(2)}`}
            </span>
          }
          onClick={() => navAndClose('/wallet')}
        />
        {/* Discoverability shortcut for the Coupons page. The map's
            QuickActions popup also surfaces /coupons, but a driver
            arriving via Profile had no path to find their coupons.
            `name="ticket"` reserves the slot for a future Lottie
            file at public/icons/ticket.json — until that lands the
            row renders the Lucide Ticket fallback, same pattern as
            every other SheetIcon row in this sheet. */}
        <ListRow
          icon={<SheetIcon name="ticket" fallback={<Ticket size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS * 2} />}
          label={t('money.coupons')}
          onClick={() => navAndClose('/coupons')}
        />
        {/* Cards & Fobs — register a physical card or key fob via the
            tap-capture self-registration flow at /my-fobs. Warren's
            order: Payments, Loyalty Wallet, Coupons, Cards & Fobs. */}
        <ListRow
          icon={<SheetIcon name="nfc" fallback={<Nfc size={SHEET_ICON_SIZE_PX} />} staggerDelayMs={SHEET_STAGGER_MS} />}
          label={t('money.fobs')}
          onClick={() => navAndClose('/my-fobs')}
        />
      </BottomSheet>

      {avatarPickerOpen && (
        <AvatarPicker
          currentStyle={profile?.avatar_style ?? 'avataaars-neutral'}
          currentSeed={profile?.avatar_seed ?? user?.email ?? 'default'}
          onClose={() => setAvatarPickerOpen(false)}
        />
      )}

      {switchBuildingOpen && (
        <SwitchBuildingModal
          // Manual entry: no pre-resolved code/tenant. The user
          // types the 6-char code in the modal's first phase; the
          // modal looks it up against the public endpoint.
          currentBuilding={accountQ.data?.current_tenant
            ? {
                name: accountQ.data.current_tenant.name,
                isDefault: accountQ.data.current_tenant.is_default,
              }
            : null}
          onNavigate={onNavigate}
          onClose={() => setSwitchBuildingOpen(false)}
          allowManualCodeEntry
        />
      )}
    </div>
  );
}

/**
 * Single tappable row used as the entry point for each category.
 * Visually larger than a ListRow (64px tall, brand-tinted icon
 * pill) and no meta slot — the sub-items live in the bottom
 * sheet that opens on tap.
 *
 * The optional `trailing` slot replaces the default chevron when
 * provided — used by the Fleet Mode toggle row (Phase 3) to render
 * a status pill ("ACTIVE" / "SWITCH TO ACTIVATE") instead of the
 * navigation chevron. The toggle is a one-tap flip with toast
 * feedback, not a navigation, so a chevron would mislead.
 */
function CategoryRow({
  icon,
  label,
  onClick,
  onLongPress,
  trailing,
  subtitle,
  dataTour,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  /** Long-press (~500ms) handler — pins the sheet. Omitted for cards that
   *  navigate (EVI Rewards) or toggle (Fleet Mode): those get no pin. */
  onLongPress?: () => void;
  trailing?: React.ReactNode;
  /** One-line description shown under the label. Rendered only at the
   *  desktop breakpoint (hidden on mobile via CSS) so the mobile row is
   *  unchanged; on desktop it fills out the larger 2x2 grid card. */
  subtitle?: string;
  /** Guided-tour anchor (data-tour) so a step can spotlight this row or fly
   *  EVII's orb to it. */
  dataTour?: string;
}) {
  // Held taps navigate on release (see usePressNav) — a plain onClick is
  // suppressed by iOS on a touch-and-hold.
  const press = usePressNav(onClick, onLongPress);
  return (
    <button
      type="button"
      className={styles.categoryRow}
      data-tour={dataTour}
      // Suppress the native long-press callout / right-click menu so the hold
      // reads as a pin gesture (pairs with the touch-callout/user-select CSS).
      onContextMenu={(e) => e.preventDefault()}
      {...press}
    >
      <span aria-hidden className={styles.categoryIcon}>{icon}</span>
      <span className={styles.categoryText}>
        <span className={styles.categoryLabel}>{label}</span>
        {subtitle && <span className={styles.categorySubtitle}>{subtitle}</span>}
      </span>
      {trailing !== undefined ? (
        <span className={styles.categoryChevron}>{trailing}</span>
      ) : (
        <span aria-hidden className={styles.categoryChevron}>
          <ChevronRight size={18} />
        </span>
      )}
    </button>
  );
}

/**
 * Mode status pill — trailing slot for the Fleet Mode CategoryRow
 * and the Account sheet "Public Charging" ListRow.
 *
 * Visual: a status dot + uppercase label. Active state uses
 * --evi-info (blue) for fleet mode and --evi-accent (brand green)
 * for public/active states; chose --evi-info over --evi-busy for the
 * fleet pill so the amber-busy semantic ("session active /
 * charger occupied") doesn't collide with the unrelated "you're in
 * fleet mode" identity signal. No new token was added — both colors
 * already exist in @evi/skins per Phase 1.
 *
 * disabled=true during an in-flight mode flip; the pill goes
 * slightly muted to acknowledge the tap landed without locking the
 * row out of further taps (the hook itself short-circuits redundant
 * mutations).
 */
function ModeStatusPill({
  active,
  activeLabel,
  inactiveLabel,
  disabled,
}: {
  active: boolean;
  activeLabel: string;
  inactiveLabel: string;
  disabled?: boolean;
}) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 9px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        opacity: disabled ? 0.6 : 1,
        color: active ? 'var(--evi-accent)' : 'var(--evi-text-mute)',
        background: active
          ? 'color-mix(in srgb, var(--evi-accent) 14%, transparent)'
          : 'color-mix(in srgb, var(--evi-text-mute) 12%, transparent)',
        border: `1px solid ${active
          ? 'color-mix(in srgb, var(--evi-accent) 38%, transparent)'
          : 'color-mix(in srgb, var(--evi-text-mute) 28%, transparent)'}`,
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: active ? 'var(--evi-accent)' : 'var(--evi-text-mute)',
          // Subtle glow on the active dot so it reads as "lit up"
          // rather than just a fill swatch. Mirrors the StatusPill
          // primitive's accent treatment.
          boxShadow: active
            ? '0 0 6px color-mix(in srgb, var(--evi-accent) 60%, transparent)'
            : 'none',
        }}
      />
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}
